import { registerTranslationAdapter } from '@heidi/runtime/i18n';
import { DictationCleanupModes, OrganisationRole } from '@heidi/schema/ml-scribe';
import i18next from 'i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { initReactI18next, useTranslation as useI18nextTranslation } from 'react-i18next';

import { useIsScribeEnableRtlEnabled } from '@/features/flags';
import { GET_USER } from '@/hooks/query/useQueryUser';
import { useQueryUserLanguage } from '@/hooks/useQueryUserLanguage';
import en from '@/locales/en/common.json';
import { useOsLocaleValue } from '@/providers/os-locale-context.utils';
import { isTauri } from '@/tauri/utils';
import type { TranslationKeys } from '@/types/translation-keys.generated';
import { getDeploymentDefaultLocale } from '@/utils/i18n/getDeploymentDefaultLocale';
import { languageLoaders } from '@/utils/i18n/languageLoaders';
import { applyPseudolocalisationToLegacyCommonJson } from '@/utils/i18n/pseudolocaliseLegacyCommonJson';
import {
  DEFAULT_LOCALE,
  getSupportedLocaleOrDefault,
  SupportedLocale,
} from '@/utils/i18n/supportedLocales';
import { logger } from './logger/logger';
import { sanitizeError } from './logger/sanitizeError';
import { reactQueryClient } from './reactQueryClient';

export type { TranslationKeys } from '@/types/translation-keys.generated';
export { languageLoaders } from '@/utils/i18n/languageLoaders';
export type TranslationFunction = (
  key: TranslationKeys,
  options?: Record<string, unknown>
) => string;

type UseTranslationsOptions = {
  /**
   * Accepts canonical supported locales and locale aliases such as `fr` or
   * `pt_BR`. The value is normalized to a supported locale internally.
   */
  localeOverride?: string;
};

export const getOrganisationRoleTranslationKey = (
  role: OrganisationRole
): TranslationKeys => {
  const roleToKeyMap: Record<OrganisationRole, TranslationKeys> = {
    [OrganisationRole.EXECUTIVE_TEAM]: 'shared.organisationRole.ExecutiveTeam',
    [OrganisationRole.INDIVIDUAL_CLINICIAN]:
      'shared.organisationRole.IndividualClinician',
    [OrganisationRole.LEAD]: 'shared.organisationRole.Lead',
    [OrganisationRole.OWNER_OR_PRACTICE_MANAGER]:
      'shared.organisationRole.OwnerOrPracticeManager',
  };
  return roleToKeyMap[role];
};

export const getDictationCleanupModeTranslationKey = (
  mode: DictationCleanupModes
): TranslationKeys => {
  const modeToKeyMap: Record<DictationCleanupModes, TranslationKeys> = {
    [DictationCleanupModes.SMART]: 'dictationOptions.SMART',
    [DictationCleanupModes.WORD_FOR_WORD]: 'dictationOptions.WORD_FOR_WORD',
  };
  return modeToKeyMap[mode];
};

void i18next.use(initReactI18next).init({
  resources: {
    en: { common: en },
    // Lazy load the rest
  },
  defaultNS: 'common',
  fallbackLng: DEFAULT_LOCALE,
  interpolation: {
    escapeValue: false,
  },
});

/**
 * Key typing for the `@heidi/runtime/i18n` door (OpenSpec
 * `monorepo-ui-intake-floor`, rulings row 1). The door cannot import the
 * app-generated union without becoming app-coupled, so it exposes an interface
 * to merge into; un-augmented its key type is `string`.
 *
 * This one declaration is the whole of the i18n team's choice in row 1:
 * augmented — as here — package UI gets exactly the typing call sites have at
 * `@/utils/translations` today; delete it and keys become `string`, still
 * guarded by the Translation Keys ratchet
 * (`scripts/check-use-translations-usage.ts`). Preserving today's typing is the
 * conservative default; neither option changes the door.
 */
declare module '@heidi/runtime/i18n' {
  interface TranslationKeyRegistry {
    key: TranslationKeys;
  }
}

/**
 * Register the i18next implementation behind the `@heidi/runtime/i18n` door,
 * at module scope, immediately after the singleton is initialised above.
 *
 * This file IS the app's i18n init site (measured — nothing else calls
 * `i18next.init`, and there is no `I18nextProvider` in the main tree), so
 * registering here makes adapter availability and singleton readiness the same
 * event. The door's contract requires registration before anything renders;
 * `_app.tsx` imports `TranslationsProvider` from this module, so loading the
 * app loads the registration.
 *
 * Neither member reimplements anything: the hook calls the app hook below, and
 * the non-React accessor delegates to the same singleton bare `i18next.t(...)`
 * call sites already use.
 *
 * The hook member CALLS `useTranslations()` rather than reaching for
 * `react-i18next` itself, and that is what makes the door's `locale` the app's
 * answer instead of a second one. The resolved locale is a composite of
 * `router.locale`, the user's language setting, the OS/browser locale and the
 * RTL flag, in that precedence — reproducing the ladder here would give a
 * package UI file a locale the app disagrees with the moment the precedence
 * changes. It also makes the specifier rewrite exact for the ~180 call sites the
 * codemod moved: they read the same hook they always did, one indirection later.
 */
registerTranslationAdapter({
  useTranslations: () => {
    const { t, locale } = useTranslations();
    return { t, locale };
  },
  t: (key, options) => (options ? i18next.t(key, { ...options }) : i18next.t(key)),
});

/**
 * @deprecated Prefer `useIntl()` or `<FormattedMessage />` with ICU messages from react-intl.
 *
 * Focus on authoring the `defaultMessage` content. Message `id` values should be
 * auto-generated with linter rather than manually chosen.
 *
 * ICU syntax reference:
 * https://formatjs.github.io/docs/core-concepts/icu-syntax
 *
 * Example with `useIntl()`:
 * ```tsx
 * const intl = useIntl();
 * const title = intl.formatMessage(
 *   {
 *     id: 'auto-generated-id',
 *     defaultMessage: 'Conversation with {name}',
 *   },
 *   { name }
 * );
 * ```
 * Example with `<FormattedMessage />`:
 * ```tsx
 * <FormattedMessage
 *   id="auto-generated-id"
 *   defaultMessage="Conversation with {name}"
 *   values={{ name }}
 * />
 * ```
 */
export const useTranslations = ({ localeOverride }: UseTranslationsOptions = {}) => {
  const router = useRouter();
  const userLanguageSetting = useQueryUserLanguage();
  const { locale = DEFAULT_LOCALE } = router;
  const isRtlLocaleFeatureEnabled = useIsScribeEnableRtlEnabled();
  // Pre-auth fallback for routes where Next.js i18n routing can't provide the
  // intended locale: desktop static export (`router.locale` pinned to `en`),
  // web's `default` locale branch, and on-prem web, where the build strips the
  // i18n config (`onprem/patch_config.cjs`) so `router.locale` is `undefined`.
  // Keeps legacy i18next strings and Auth0 `ui_locales` in step with the
  // OS/browser language.
  // SCR-5924: read the hoisted value from context (computed once in
  // `HeidiIntlProvider`) instead of calling `useOsLocale()` at every call site.
  const osLocale = useOsLocaleValue();
  const osLocaleFallback =
    isTauri() || !router.locale || router.locale === 'default' ? osLocale : undefined;

  const { t: i18nextT, i18n } = useI18nextTranslation();
  const localeChangeRequestIdRef = useRef(0);

  const t = useCallback<TranslationFunction>(
    (key, options) => {
      if (options) {
        return i18nextT(key, { ...options });
      }
      return i18nextT(key);
    },
    [i18nextT]
  );

  // The browser is the authority on a user's language, so the deployment
  // default sits *below* the OS/browser locale — it only fills the gap where
  // the OS expressed nothing. `undefined` on cloud and on any on-prem build
  // with no configured language.
  const deploymentDefaultLocale = getDeploymentDefaultLocale({
    isRtlLocaleFeatureEnabled,
  });

  const resolvedLocale = getSupportedLocaleOrDefault(
    localeOverride ||
      userLanguageSetting ||
      osLocaleFallback ||
      deploymentDefaultLocale ||
      locale,
    { isRtlLocaleFeatureEnabled }
  );

  return {
    t,
    i18n,
    /** Canonical BCP-47 segment matching Next.js `i18n.locales` (e.g. `fr-FR`, `en`). */
    locale: resolvedLocale,
    /**
     * `keepQuery` carries `router.query` in the href as well as in `as`. Next
     * reads `router.query` from the href, so the default call keeps the query
     * in the address bar but empties `router.query`, and the middleware probe
     * on the route change goes out without it. A page that reads its inputs
     * from the query (the hosted onboarding page) asks for it; the in-app
     * flow is left as it was.
     */
    changeLocale: (newLocale: string, options?: { keepQuery?: boolean }) => {
      const validLocale = getSupportedLocaleOrDefault(newLocale, {
        isRtlLocaleFeatureEnabled,
      });
      const requestId = ++localeChangeRequestIdRef.current;
      void (async () => {
        try {
          const resources = await languageLoaders[validLocale]?.();
          if (requestId !== localeChangeRequestIdRef.current) return;
          if (resources) {
            let bundle: unknown = resources.default ?? resources;
            if (validLocale === SupportedLocale.EnPs) {
              bundle = await applyPseudolocalisationToLegacyCommonJson(bundle);
            }
            i18n.addResourceBundle(validLocale, 'common', bundle, true, true);
          }
          await i18n.changeLanguage(validLocale);
          if (requestId !== localeChangeRequestIdRef.current) return;
          void router.push(
            options?.keepQuery
              ? { pathname: router.pathname, query: router.query }
              : router.pathname,
            router.asPath,
            { locale: validLocale }
          );
        } catch (e) {
          logger.error('Failed to change locale', e);
        }
      })();
    },
  };
};

/**
 * We only want a singleton to handle loading new language
 */
export const TranslationsProvider = () => {
  const router = useRouter();
  const userLanguageSetting = useQueryUserLanguage();
  const { locale = DEFAULT_LOCALE } = router;
  const isRtlLocaleFeatureEnabled = useIsScribeEnableRtlEnabled();
  // Pre-auth fallback for routes where Next.js i18n routing can't provide the
  // intended locale: desktop static export (`router.locale` pinned to `en`),
  // web's `default` locale branch, and on-prem web, where the build strips the
  // i18n config (`onprem/patch_config.cjs`) so `router.locale` is `undefined`.
  // Loads the OS/browser-language catalog into the global i18next instance.
  // SCR-5924: read the hoisted value from context instead of `useOsLocale()`.
  const osLocale = useOsLocaleValue();
  const osLocaleFallback =
    isTauri() || !router.locale || router.locale === 'default' ? osLocale : undefined;

  const { i18n } = useI18nextTranslation();
  const switchQueueRef = useRef<Promise<void>>(Promise.resolve());
  const switchRequestIdRef = useRef(0);
  const latestTargetRef = useRef('');

  useEffect(() => {
    const targetLanguage = getSupportedLocaleOrDefault(
      userLanguageSetting ||
        osLocaleFallback ||
        getDeploymentDefaultLocale({ isRtlLocaleFeatureEnabled }) ||
        locale ||
        DEFAULT_LOCALE,
      { isRtlLocaleFeatureEnabled }
    );
    const requestId = ++switchRequestIdRef.current;
    const target = targetLanguage.toString();
    latestTargetRef.current = target;

    // Catalog import and changeLanguage are async, and these deps can change
    // mid-flight (OS locale first, saved display language later). The queue
    // stops an in-flight changeLanguage from landing after a newer target.
    // The request id drops a switch that has not called changeLanguage yet.
    // After changeLanguage, skip the user refetch only when a newer switch
    // will change language again. A newer switch whose target is already
    // active returns before refetching, so this step still does.
    switchQueueRef.current = switchQueueRef.current
      .then(async () => {
        if (requestId !== switchRequestIdRef.current) return;
        if (i18n.language === target) return;
        const resources = await languageLoaders[targetLanguage]?.();
        if (requestId !== switchRequestIdRef.current) return;
        let bundle: unknown = resources.default ?? resources;
        if (targetLanguage === SupportedLocale.EnPs) {
          bundle = await applyPseudolocalisationToLegacyCommonJson(bundle);
          if (requestId !== switchRequestIdRef.current) return;
        }
        i18n.addResourceBundle(targetLanguage, 'common', bundle);
        await i18n.changeLanguage(targetLanguage);
        const superseded = requestId !== switchRequestIdRef.current;
        if (superseded && i18n.language !== latestTargetRef.current) return;
        await invalidateCurrentUserLanguage();
      })
      .catch((e: unknown) => {
        logger.error('Failed to switch language', { error: sanitizeError(e) });
      });
  }, [i18n, isRtlLocaleFeatureEnabled, locale, osLocaleFallback, userLanguageSetting]);
  return null;
};

export const invalidateCurrentUserLanguage = async () => {
  // Only the user query needs an explicit refetch here (server-localized
  // fields). Specialties re-key on the active language (useQuerySpecialties),
  // but on a LOCAL display-language switch that self-refetch races the
  // settings PATCH, so the settings delegate (useMutateUpdateSettings →
  // onDisplayLanguageChanged) calls `invalidateSpecialties` post-save rather
  // than this helper doing it for every caller.
  await reactQueryClient.invalidateQueries({ queryKey: [GET_USER] });
};
